// The behavior of Bootstrap 3's JavaScript (dropdowns, collapse, tabs, modals,
// tooltips, popovers) without jQuery. The markup, data attributes and CSS
// classes are Bootstrap's own (bootstrap-sass keeps providing the CSS), so
// views did not change. Everything is delegated from the document: Turbo
// Streams and React render markup at any time and need no initialization.
//
// Supported markup:
//   [data-toggle=dropdown]            toggles .open on the parent (.dropdown)
//   [data-toggle=collapse]            data-target or href, optional data-parent
//   [data-toggle=tab]                 href is the id of a .tab-pane
//   [data-toggle=modal], [data-dismiss=modal]
//   [data-show=tooltip]               data-title, data-placement (default top)
//   [data-show=popover]               data-content (HTML), data-title,
//                                     data-placement (default right); a
//                                     [data-dismiss=popover] inside closes it
//
// Not carried over: keyboard navigation inside dropdown menus, focus trap of
// modals, tooltip/popover auto placement and the data-trigger options.

import { sanitizeHtml } from "./sanitize";

const TRANSITION = 350;

// -- Dropdowns ---------------------------------------------------------------

function closeDropdowns(except) {
  document.querySelectorAll(".open > [data-toggle=dropdown]").forEach((toggle) => {
    const parent = toggle.parentElement;
    if (parent === except) return;
    parent.classList.remove("open");
    toggle.setAttribute("aria-expanded", "false");
  });
}

function toggleDropdown(toggle) {
  const parent = toggle.parentElement;
  const opening = !parent.classList.contains("open");
  closeDropdowns(opening ? parent : null);
  parent.classList.toggle("open", opening);
  toggle.setAttribute("aria-expanded", String(opening));
}

// -- Collapse ----------------------------------------------------------------

function collapseTarget(trigger) {
  const selector = trigger.dataset.target || trigger.getAttribute("href");
  return selector && selector.startsWith("#") ? document.querySelector(selector) : null;
}

function transitionEnd(element, callback) {
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    element.removeEventListener("transitionend", finish);
    callback();
  };
  element.addEventListener("transitionend", finish);
  setTimeout(finish, TRANSITION);
}

function markTriggers(target, expanded) {
  document
    .querySelectorAll(`[data-toggle=collapse][href="#${target.id}"], [data-toggle=collapse][data-target="#${target.id}"]`)
    .forEach((trigger) => {
      trigger.classList.toggle("collapsed", !expanded);
      trigger.setAttribute("aria-expanded", String(expanded));
    });
}

function showCollapse(target) {
  if (target.classList.contains("in") || target.classList.contains("collapsing")) return;
  target.classList.remove("collapse");
  target.classList.add("collapsing");
  target.style.height = "0px";
  markTriggers(target, true);
  target.style.height = `${target.scrollHeight}px`;
  transitionEnd(target, () => {
    target.classList.remove("collapsing");
    target.classList.add("collapse", "in");
    target.style.height = "";
  });
}

function hideCollapse(target) {
  if (!target.classList.contains("in") || target.classList.contains("collapsing")) return;
  target.style.height = `${target.getBoundingClientRect().height}px`;
  target.offsetHeight; // force a reflow so the height change is animated
  target.classList.add("collapsing");
  target.classList.remove("collapse", "in");
  markTriggers(target, false);
  target.style.height = "0px";
  transitionEnd(target, () => {
    target.classList.remove("collapsing");
    target.classList.add("collapse");
    target.style.height = "";
  });
}

function toggleCollapse(trigger) {
  const target = collapseTarget(trigger);
  if (!target) return;
  if (target.classList.contains("collapsing")) return;
  if (!target.classList.contains("in")) {
    const parent = trigger.dataset.parent && document.querySelector(trigger.dataset.parent);
    if (parent) {
      parent.querySelectorAll(":scope > .panel > .in").forEach((open) => hideCollapse(open));
    }
    showCollapse(target);
  } else {
    hideCollapse(target);
  }
}

// -- Tabs --------------------------------------------------------------------

export function showTab(link) {
  const item = link.closest("li");
  if (item && item.classList.contains("active")) return;
  const pane = document.querySelector(link.getAttribute("href"));
  if (!pane) return;

  const list = link.closest("ul");
  list?.querySelectorAll(":scope > .active").forEach((li) => li.classList.remove("active"));
  item?.classList.add("active");
  pane.parentElement
    .querySelectorAll(":scope > .tab-pane.active")
    .forEach((other) => other.classList.remove("active"));
  pane.classList.add("active");
  link.dispatchEvent(new CustomEvent("tab:shown", {bubbles: true}));
}

// -- Modals ------------------------------------------------------------------

export function showModal(modal) {
  if (modal.classList.contains("in")) return;
  // Never two backdrops for one modal (a second show while the first is still
  // fading out would lose the reference to the first one).
  modal._backdrop?.remove();
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop fade";
  document.body.appendChild(backdrop);
  modal._backdrop = backdrop;
  document.body.classList.add("modal-open");

  modal.style.display = "block";
  modal.removeAttribute("aria-hidden");
  modal.scrollTop = 0;
  modal.offsetWidth; // force a reflow so the fade is animated
  modal.classList.add("in");
  backdrop.classList.add("in");
  if (!modal.contains(document.activeElement)) modal.focus();
}

export function hideModal(modal) {
  if (!modal.classList.contains("in")) return;
  modal.classList.remove("in");
  modal._backdrop?.classList.remove("in");
  transitionEnd(modal, () => {
    modal.style.display = "none";
    modal.setAttribute("aria-hidden", "true");
    removeBackdrop(modal);
    modal.dispatchEvent(new CustomEvent("modal:hidden", {bubbles: true}));
  });
}

// Drops the backdrop of a modal that is leaving the page without a transition.
// With no modal left open nothing may dim the page: any other backdrop is a
// leftover and goes too, together with the scroll lock.
export function removeBackdrop(modal) {
  modal._backdrop?.remove();
  modal._backdrop = null;
  if (document.querySelector(".modal.in")) return;
  document.querySelectorAll(".modal-backdrop").forEach((backdrop) => backdrop.remove());
  document.body.classList.remove("modal-open");
}

function openModals() {
  return Array.from(document.querySelectorAll(".modal.in"));
}

// -- Tooltips and popovers ---------------------------------------------------

const PLACEMENT_GAP = 5;

function place(tip, element, placement) {
  const rect = element.getBoundingClientRect();
  const left = rect.left + window.scrollX;
  const top = rect.top + window.scrollY;
  tip.style.display = "block";
  tip.style.top = "0px";
  tip.style.left = "0px";
  const width = tip.offsetWidth;
  const height = tip.offsetHeight;
  const positions = {
    top: [top - height - PLACEMENT_GAP, left + rect.width / 2 - width / 2],
    bottom: [top + rect.height + PLACEMENT_GAP, left + rect.width / 2 - width / 2],
    left: [top + rect.height / 2 - height / 2, left - width - PLACEMENT_GAP],
    right: [top + rect.height / 2 - height / 2, left + rect.width + PLACEMENT_GAP],
  };
  let [y, x] = positions[placement];
  // Keep the box on screen; the arrow stays on the element (as Bootstrap does).
  const maxX = document.documentElement.clientWidth + window.scrollX - width;
  const shifted = Math.max(window.scrollX, Math.min(x, maxX));
  tip.style.top = `${y}px`;
  tip.style.left = `${shifted}px`;
  // Next to the screen edge the text may wrap differently than at the origin
  // where it was measured; place again with the real height.
  if (tip.offsetHeight !== height && (placement === "top" || placement === "left" || placement === "right")) {
    const diff = tip.offsetHeight - height;
    y = placement === "top" ? y - diff : y - diff / 2;
    tip.style.top = `${y}px`;
  }
  const arrow = tip.querySelector(".tooltip-arrow, .arrow");
  if (arrow && shifted !== x && (placement === "top" || placement === "bottom")) {
    arrow.style.left = `${rect.left + window.scrollX + rect.width / 2 - shifted}px`;
  }
}

function showTip(tip) {
  tip.offsetWidth; // force a reflow so the fade is animated
  tip.classList.add("in");
}

function removeTip(tip) {
  tip.classList.remove("in");
  setTimeout(() => tip.remove(), 150);
}

const tooltips = new Map(); // element -> tooltip node

function showTooltip(element) {
  if (tooltips.has(element)) return;
  const title = element.dataset.title;
  if (!title) return;
  const placement = element.dataset.placement || "top";
  const tip = document.createElement("div");
  tip.className = `tooltip fade ${placement}`;
  tip.setAttribute("role", "tooltip");
  const arrow = document.createElement("div");
  arrow.className = "tooltip-arrow";
  const inner = document.createElement("div");
  inner.className = "tooltip-inner";
  inner.textContent = title;
  tip.append(arrow, inner);
  document.body.appendChild(tip);
  place(tip, element, placement);
  tooltips.set(element, tip);
  showTip(tip);
  watchRemovals();
}

function hideTooltip(element) {
  const tip = tooltips.get(element);
  if (!tip) return;
  tooltips.delete(element);
  removeTip(tip);
}

// A tooltip must not outlive its element: a Turbo Stream may replace the row
// the pointer is over, and no mouseout is sent for a removed element.
let observer = null;
function watchRemovals() {
  if (observer) return;
  observer = new MutationObserver(() => {
    tooltips.forEach((_tip, element) => {
      if (!element.isConnected) hideTooltip(element);
    });
    if (tooltips.size === 0) {
      observer.disconnect();
      observer = null;
    }
  });
  observer.observe(document.body, {childList: true, subtree: true});
}

const popovers = new Map(); // element -> popover node

function togglePopover(element) {
  if (popovers.has(element)) {
    hidePopover(element);
    return;
  }
  const placement = element.dataset.placement || "right";
  const tip = document.createElement("div");
  tip.className = `popover fade ${placement}`;
  tip.setAttribute("role", "tooltip");
  const arrow = document.createElement("div");
  arrow.className = "arrow";
  const title = document.createElement("h3");
  title.className = "popover-title";
  title.textContent = element.dataset.title || "";
  title.hidden = !element.dataset.title;
  const content = document.createElement("div");
  content.className = "popover-content";
  content.innerHTML = sanitizeHtml(element.dataset.content || "");
  tip.append(arrow, title, content);
  document.body.appendChild(tip);
  tip._element = element;
  place(tip, element, placement);
  popovers.set(element, tip);
  showTip(tip);
}

function hidePopover(element) {
  const tip = popovers.get(element);
  if (!tip) return;
  popovers.delete(element);
  removeTip(tip);
}

// -- Events ------------------------------------------------------------------

document.addEventListener("click", (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const toggle = target.closest("[data-toggle]");
  if (toggle) {
    const kind = toggle.dataset.toggle;
    if (kind === "dropdown") {
      event.preventDefault();
      toggleDropdown(toggle);
      return;
    }
    if (kind === "collapse") {
      event.preventDefault();
      toggleCollapse(toggle);
      return;
    }
    if (kind === "tab") {
      event.preventDefault();
      showTab(toggle);
      return;
    }
    if (kind === "modal") {
      event.preventDefault();
      const modal = document.querySelector(toggle.dataset.target);
      if (modal) showModal(modal);
      return;
    }
  }

  const dismiss = target.closest("[data-dismiss]");
  if (dismiss) {
    if (dismiss.dataset.dismiss === "modal") {
      event.preventDefault();
      const modal = dismiss.closest(".modal");
      if (modal) hideModal(modal);
      return;
    }
    if (dismiss.dataset.dismiss === "popover") {
      event.preventDefault();
      const tip = dismiss.closest(".popover");
      if (tip?._element) hidePopover(tip._element);
      return;
    }
  }

  const popover = target.closest("[data-show=popover]");
  if (popover) {
    event.preventDefault();
    togglePopover(popover);
    return;
  }

  // A click on the dark area around a modal closes it.
  if (target.classList.contains("modal") && target.classList.contains("in")) {
    hideModal(target);
    return;
  }

  // A click anywhere else closes the open dropdowns.
  closeDropdowns(null);
});

document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape") return;
  closeDropdowns(null);
  const modals = openModals();
  if (modals.length) hideModal(modals[modals.length - 1]);
});

function tooltipElement(event) {
  return event.target instanceof Element ? event.target.closest("[data-show=tooltip]") : null;
}

document.addEventListener("mouseover", (event) => {
  const element = tooltipElement(event);
  if (element) showTooltip(element);
});

document.addEventListener("mouseout", (event) => {
  const element = tooltipElement(event);
  if (!element) return;
  // Moving between children of the element is not leaving it.
  if (event.relatedTarget instanceof Node && element.contains(event.relatedTarget)) return;
  hideTooltip(element);
});

document.addEventListener("focusin", (event) => {
  const element = tooltipElement(event);
  if (element) showTooltip(element);
});

document.addEventListener("focusout", (event) => {
  const element = tooltipElement(event);
  if (element) hideTooltip(element);
});

// -- Turbo cache -------------------------------------------------------------

// Turbo stores a snapshot of a page when it is left and shows it again on
// back/forward. Transient UI must not be in it: an open dropdown, tooltips and
// popovers hanging in the body, an open modal with its backdrop. Streams append
// `bootstrap-modal` elements that open themselves when connected, so those are
// dropped from the snapshot altogether.
document.addEventListener("turbo:before-cache", () => {
  closeDropdowns(null);
  tooltips.forEach((tip) => tip.remove());
  tooltips.clear();
  popovers.forEach((tip) => tip.remove());
  popovers.clear();
  document.querySelectorAll(".tooltip, .popover").forEach((tip) => tip.remove());
  document.querySelectorAll("[data-controller~=bootstrap-modal]").forEach((modal) => modal.remove());
  document.querySelectorAll(".modal.in").forEach((modal) => {
    modal.classList.remove("in");
    modal.style.display = "none";
    modal.setAttribute("aria-hidden", "true");
    removeBackdrop(modal);
  });
  document.querySelectorAll(".modal-backdrop").forEach((backdrop) => backdrop.remove());
  document.body.classList.remove("modal-open");
});
